import { CognitoUser } from 'amazon-cognito-identity-js';
import API, { post } from '../api/baseUrl';
import Swal from 'sweetalert2/dist/sweetalert2.js';

// import styles separately
import 'sweetalert2/dist/sweetalert2.css';
import axios from 'axios';
import { Auth } from 'aws-amplify';
import { getUserAccess, resetAccessCache } from './accessControl';

class AuthState {
  static get tempPass() {
    return this._tempPass;
  }

  static set tempPass(value) {
    this._tempPass = value;
  }
  static get login_type() {
    return this._login_type;
  }

  static set login_type(value) {
    this._login_type = value;
  }
  static get pin() {
    return this._pin;
  }

  static set pin(value) {
    this._pin = value;
  }
  static get emailCode() {
    return this._emailCode;
  }

  static set emailCode(value) {
    this._emailCode = value;
  }
  static get email_verified() {
    return this._email_verified;
  }

  static set email_verified(value) {
    this._email_verified = value;
  }
  static get email() {
    return this._email;
  }

  static set email(value) {
    this._email = value;
  }
  static get phone() {
    return this._phone;
  }

  static set phone(value) {
    this._phone = value;
  }

  static get cognitoUser() {
    return this._cognitoUser;
  }

  static set cognitoUser(value) {
    this._cognitoUser = value;
  }

  static get isRegistering() {
    return this._isRegistering;
  }

  static set isRegistering(value) {
    this._isRegistering = value;
  }

  static _cognitoUser;
  static _phone;
  static _emailCode;
  static _email;
  static _email_verified;
  static _isRegistering;
  static _pin;
  static _login_type;
  static _tempPass;
}

function destroy() {
  resetAccessCache();
  AuthState.cognitoUser = null;
  AuthState.email = '';
  AuthState.email_verified = null;
  AuthState.login_type = null;
  AuthState.isRegistering = null;
  AuthState.phone = '';
  AuthState.phone_code = '';
  AuthState.pin = '';
  AuthState.tempPass = '';
}

function reset() {
  AuthState.phone = '';
  AuthState.phone_code = '';
  AuthState.pin = '';
  AuthState.tempPass = '';
}

async function changePin(currentPin, newPin) {
  const user = await Auth.currentAuthenticatedUser();
  await Auth.changePassword(user, currentPin + '00', newPin + '00')
    .then(async (result) => {
      Swal.fire({
        icon: 'success',
        text: `Pin updated`,
        showConfirmButton: true,
      });
      return result;
    })
    .catch((e) => {
      if (e.code == 'LimitExceededException') {
        Swal.fire({
          icon: 'error',
          text: 'Maximum number of tries exceeded. Please try again later.',
          showConfirmButton: true,
        });
      } else {
        Swal.fire({
          icon: 'warning',
          text: e,
          showConfirmButton: true,
        });
      }
      console.error(e);
      return e;
    });
}

//For later use
async function forgotPin() {
  const user = await Auth.currentAuthenticatedUser();
  return await Auth.forgotPassword(user.username)
    .then(async (result) => {
      Swal.fire({
        icon: 'info',
        text: `Verification code sent by ${result.CodeDeliveryDetails.AttributeName}.`,
        showConfirmButton: true,
      });
      return result;
    })
    .catch((e) => {
      Swal.fire({
        icon: 'warning',
        text: e,
        showConfirmButton: true,
      });
      console.error(e);
      return e;
    });
}

async function forgotPinChange(verificationCode, newPin) {
  const user = await Auth.currentAuthenticatedUser();
  return await Auth.forgotPasswordSubmit(
    user.username,
    verificationCode,
    newPin + '00',
  )
    .then(async (result) => {
      Swal.fire({
        icon: 'success',
        text: `Pin updated`,
        showConfirmButton: true,
      });
      return result;
    })
    .catch((e) => {
      if (e.code == 'CodeMismatchException') {
        Swal.fire({
          icon: 'warning',
          text: `Invalid Verification code.`,
          showConfirmButton: true,
        });
      } else if (e.code == 'LimitExceededException') {
        Swal.fire({
          icon: 'warning',
          text: `Pin change limit exceeded. Please try again later.`,
          showConfirmButton: true,
        });
      } else {
        Swal.fire({
          icon: 'warning',
          text: e,
          showConfirmButton: true,
        });
      }
      console.error(e);
      return e;
    });
}

/**
 *
 * @param {*} payload
 * @returns {Promise<CognitoUser|string|null>}
 */
export async function cogSignIn(payload) {
  //if (!payload.password) {
  Auth.configure({ authenticationFlowType: 'CUSTOM_AUTH' });
  // } else {
  //     Auth.configure({authenticationFlowType: 'USER_SRP_AUTH'});
  // }
  return await Auth.signIn(payload)
    .then(async (result) => {
      AuthState.cognitoUser = result;
      return result;
    })
    .catch((e) => {
      console.error(e);
      if (payload.password) {
        throw e;
      }
      return null;
    });
}

/**
 * Get user location and device info
 * @returns {Promise<Object>}
 *   - loc_data: location status
 *   - user_agent: browser user agent string
 */
async function getLocationAndDeviceInfo() {
  try {
    const res = await axios.get('https://ipapi.co/json/');
    return {
      loc_data: JSON.stringify(res.data),
      user_agent: navigator.userAgent,
    };
  } catch (error) {
    console.warn('Failed to get location info:', error);
    return {
      loc_data: JSON.stringify({}),
      user_agent: navigator.userAgent,
    };
  }
}

/**
 * Generate random password
 * @returns {string} Randomly generated 4 digit password
 */
function generateRandomPassword() {
  return (
    Math.random().toString(36).substring(7) +
    Math.random().toString(36).substring(7) +
    'aA!1'
  );
}

/**
 * Create validation data object
 * @param {string} phone_number - Phone number
 * @param {string} email - Email address
 * @param {Object} deviceInfo - Device information
 * @returns {Object} Validation data object
 */
function createValidationData(phone_number, email, deviceInfo) {
  const valData = {
    // phone_number: phone_number,
    ...deviceInfo,
  };

  if (email) {
    valData.email = email;
  }

  return valData;
}

/**
 * Create authentication payload
 * @param {string} email - Email address
 * @param {string} pin - PIN code
 * @param {Object} valData - Validation data
 * @returns {Object} Authentication payload
 */
function createAuthPayload(email, pin, valData) {
  const payload = {
    username: email,
    validationData: valData,
    attributes: {},
  };

  if (pin) {
    payload.password = pin + '00';
  }

  return payload;
}

/**
 * Handle new user registration flow
 * @param {string?} phoneNumber - Phone number
 * @param {string} email - Email address
 * @param {string} pin - PIN code
 * @param {Object} valData - Validation data
 * @returns {Promise<Object>} Cognito user object
 */
async function handleNewUserRegistration(phoneNumber, email, pin, valData) {
  try {
    const password = pin || generateRandomPassword();
    if (!pin) {
      // new signup
      AuthState.tempPass = password;
    }
    const attributes = phoneNumber ? { phone_number: phoneNumber } : {};

    await Auth.signUp({
      username: email,
      password: password,
      attributes: attributes,
      validationData: valData,
    });

    // Set registration status
    AuthState.isRegistering = true;

    // Trigger verification code sending
    AuthState.cognitoUser = await cogSignIn({
      username: email,
      validationData: valData,
    });

    return AuthState.cognitoUser;
  } catch (error) {
    console.error('New user registration failed:', error);
    throw error;
  }
}

/**
 * Handle existing user email addition flow
 * @param {string} phone_number - Phone number
 * @param {string} email - Email address
 * @returns {Promise<Object>} Cognito user object
 */
async function handleExistingUserPhoneAddition(phone_number, email) {
  try {
    const password = generateRandomPassword();
    const valData = { phone_number, email };

    AuthState.tempPass = password;

    await Auth.signUp({
      username: email,
      password: password,
      attributes: valData,
      validationData: valData,
    });

    // Set registration status
    AuthState.isRegistering = true;

    // Trigger verification code sending
    AuthState.cognitoUser = await cogSignIn({
      username: email,
      validationData: valData,
    });

    return AuthState.cognitoUser;
  } catch (error) {
    console.error('Existing user email addition failed:', error);
    throw error;
  }
}

/**
 * Handle existing user login flow
 * @param {Object} cogUser - Cognito user object
 * @returns {Object} Processed user object
 */
function handleExistingUserLogin(cogUser) {
  // Set user email information
  if (cogUser.challengeParam?.email) {
    AuthState.email = cogUser.challengeParam.email;
  }

  // Set login type
  if (cogUser.challengeParam?.login_type) {
    AuthState.login_type = cogUser.challengeParam.login_type;
  }

  // Check if user has an active session
  if (cogUser.getSignInUserSession()) {
    // This is a login flow, not registration
    AuthState.isRegistering = false;
    return cogUser;
  } else {
    // This is a partial registration flow, continue registration
    AuthState.isRegistering = true;
    return cogUser;
  }
}

// /**
//  * Handle authentication errors
//  * @param {Error} error - Error object
//  * @returns {boolean} Returns false indicating authentication failure
//  */
// function handleAuthError(error) {
//   if (
//     error ===
//     'UserLambdaValidationException: PreAuthentication failed with error Cannot authenticate users from this user pool app client.'
//   ) {
//     alert("User hasn't been verified yet");
//   }

//   if (error.message.includes('UsernameExistsException')) {
//     alert('Email already exists');
//   }

//   return false;
// }

/**
 * Main user login function
 * Handles the complete flow of user login, registration and email verification
 *
 * @param {string} email - User email address
 * @param {string?} phoneNumber - User phone number (optional)
 * @param {string?} pin - User PIN code (optional)
 * @returns {Promise<CognitoUser | null>}
 */

async function signIn(email, phoneNumber, pin) {
  try {
    // 1. Get device and location information
    const deviceInfo = await getLocationAndDeviceInfo();

    // 2. Create validation data
    const valData = createValidationData(phoneNumber, email, deviceInfo);

    // 3. Create authentication payload
    const payload = createAuthPayload(email, pin, valData);

    // 4. Attempt user authentication
    const userCheck = await cogSignIn(payload);

    // 5. Handle different flows based on user status
    if (userCheck == null && phoneNumber) {
      // New user registration flow
      return await handleNewUserRegistration(phoneNumber, email, pin, valData);
    } else if (userCheck && phoneNumber) {
      // Existing user phone addition flow
      return await handleExistingUserPhoneAddition(phoneNumber, email);
    } else if (userCheck) {
      // Existing user login flow
      return handleExistingUserLogin(userCheck);
    } else {
      // User doesn't exist and no email provided
      return null;
    }
  } catch (error) {
    // return handleAuthError(error);
    throw error;
  }
}

const confirmCode = async (code) => {
  try {
    let meta = { user_agent: navigator.userAgent };

    try {
      const res = await axios.get('https://ipapi.co/json/');
      meta.loc_data = JSON.stringify(res.data);
    } catch (locError) {
      console.warn('Failed to fetch location data:', locError);
      meta.loc_data = JSON.stringify({ error: 'Location data unavailable' });
    }

    const user = await Auth.sendCustomChallengeAnswer(
      AuthState.cognitoUser,
      code.toString(),
      meta,
    );
    return user;
  } catch (e) {
    console.log(e);
    return null;
  }
};

async function getTokens() {
  const session = await Auth.currentSession();
  if (!session) {
    signOut();
    return {
      refresh: null,
      access: null,
      id: null,
    };
  }
  return {
    refresh: session.refreshToken.token,
    access: session.accessToken.jwtToken,
    id: session.idToken.jwtToken,
  };
}

async function currentSession() {
  //return true;
  return await Auth.currentSession();
}

// getUserAccess replaced by import from ./accessControl
// Checks if user has an active Cognito session
async function checkCognito() {
  try {
    await Auth.currentAuthenticatedUser();
    return true;
  } catch {
    return false;
  }
}

async function checkUser() {
  try {
    const cognitoUser = await Auth.currentAuthenticatedUser();
    const userAccess = await getUserAccess();
    return userAccess.userStatus === 1;
  } catch {
    return false;
  }
}

async function signOut() {
  try {
    await Auth.signOut();
    destroy();
  } catch (error) {
    console.log('error signing out: ', error);
  }
}

async function clinicianSignUp(
  firstName,
  lastName,
  referralCode,
  email,
  pin,
  phone,
  clinics,
) {
  try {
    const cogUser = await Auth.currentUserPoolUser();
    const emailToUse = email || cogUser.attributes.email;
    Auth.updateUserAttributes(cogUser, {
      email: emailToUse,
      given_name: firstName,
      family_name: lastName,
    });
    if (pin && pin.length === 4) {
      Auth.changePassword(cogUser, AuthState.tempPass, pin + '00');
    }

    const payload = {
      firstName: firstName,
      lastName: lastName,
      referralCode: referralCode,
      email: emailToUse,
      clinics: clinics,
      uid: cogUser.attributes.sub,
    };

    return await post(`createClinician`, payload)
      .then((res) => {
        return res.data;
      })
      .catch((err) => {
        console.error('createClinician error:', err);
        throw err;
      });
  } catch (error) {
    console.error('error signing up:', error);
  }
}

// Remove patientSignUp function (lines 503-548)

export {
  AuthState,
  signIn,
  confirmCode,
  clinicianSignUp, // Keep clinicianSignUp
  signOut,
  currentSession,
  checkUser,
  getTokens,
  getUserAccess,
  checkCognito,
  changePin,
  forgotPin,
  forgotPinChange,
  destroy,
  reset,
};
